import { Injectable, signal, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Data,
  NavigationEnd,
  Router,
} from '@angular/router';
import { filter } from 'rxjs';
import { UnsubscribeBase } from '@core-utils';

export interface BreadcrumbInterface {
  label: string;
  url?: string;
  buttonLabel?: string;
  goBack?: string;
  disabled?: boolean;
  showList?: string;
}

@Injectable()
export class BreadcrumbService extends UnsubscribeBase {
  private router = inject(Router);

  breadcrumbs = signal<BreadcrumbInterface[]>([]);

  constructor() {
    super();
    this.addSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateBreadcrumbs();
      });

    this.updateBreadcrumbs();
  }

  private updateBreadcrumbs() {
    const root = this.router.routerState.snapshot.root;
    const breadcrumbs: BreadcrumbInterface[] = [];
    this.addBreadcrumb(root, [], breadcrumbs);
    this.breadcrumbs.set(breadcrumbs);
  }

  private addBreadcrumb(
    route: ActivatedRouteSnapshot,
    parentUrl: string[],
    breadcrumbs: BreadcrumbInterface[],
  ) {
    if (!route) return;

    const routeUrl = this.createRouteUrl(parentUrl, route);
    if (this.hasBreadcrumb(route.data)) {
      // Opt-in: drop the immediate parent crumb (e.g. "Brands") so a child
      // page can sit directly under the root without the intermediate link.
      if (route.data['hideParentBreadcrumb'] && breadcrumbs.length > 1) {
        breadcrumbs.pop();
      }
      this.addBreadcrumbToList(route.data, routeUrl, breadcrumbs);
    }

    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
  }

  private createRouteUrl(
    parentUrl: string[],
    route: ActivatedRouteSnapshot,
  ): string[] {
    return [...parentUrl, ...route.url.map((url) => url.path)];
  }

  private hasBreadcrumb(data: Data): boolean {
    return !!data['breadcrumb'];
  }

  private addBreadcrumbToList(
    data: Data,
    routeUrl: string[],
    breadcrumbs: BreadcrumbInterface[],
  ) {
    const label = this.getLabel(data);
    const url = '/' + routeUrl.join('/');
    const buttonLabel = data['buttonLabel'] || null;
    const goBack = data['goBack'] || null;
    const disabled = data['disabled'] || null;
    const showList = data['showList'] || null;

    if (!this.breadcrumbExists(breadcrumbs, label, url)) {
      breadcrumbs.push({ label, url, buttonLabel, goBack, disabled, showList });
    }
  }

  private breadcrumbExists(
    breadcrumbs: BreadcrumbInterface[],
    label: string,
    url: string,
  ): boolean {
    return breadcrumbs.some(
      (breadcrumb) => breadcrumb.label === label && breadcrumb.url === url,
    );
  }

  private getLabel(data: Data): string {
    return typeof data['breadcrumb'] === 'function'
      ? data['breadcrumb'](data)
      : data['breadcrumb'];
  }
}
